/**
 * Section screen — lists items for a given :kind.
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getSection, type SectionItem, ApiError } from "@/api";
import { useI18n } from "@/i18n";
import Skeleton from "@/components/Skeleton";
import List from "@/components/List";
import BackBar from "@/components/BackBar";

export default function Section() {
  const { kind = "" } = useParams<{ kind: string }>();
  const navigate = useNavigate();
  const { lang, t } = useI18n();

  const [items, setItems] = useState<SectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getSection(kind, lang)
      .then((data) => {
        if (!cancelled) {
          setItems(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Error loading");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [kind, lang]);

  function handleItemClick(item: SectionItem) {
    navigate(`/detail/${item.kind}/${item.id}`);
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0, transition: { duration: 0.25 } }}
      exit={{ opacity: 0, x: -24 }}
      className="min-h-screen"
      style={{ background: "var(--tg-bg)" }}
    >
      <BackBar to="/home" />

      {loading && <Skeleton count={4} />}

      {!loading && error && (
        <p className="text-center py-12 px-8" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      {!loading && !error && items.length === 0 && (
        <p className="text-center py-12 px-8" style={{ color: "var(--tg-hint)" }}>
          {t("empty_state")}
        </p>
      )}

      {!loading && !error && items.length > 0 && (
        <List items={items} onItemClick={handleItemClick} />
      )}
    </motion.div>
  );
}
